import { useCallback } from 'react';

import { styled } from '@mui/material';
import { ButtonGhost, generateUtilityClasses } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcCloseSmallRegular } from '@kep/kcds-icons';

import PanelTab from './PanelTab';

import type { ReactNode } from 'react';

export interface PanelTabBarTab {
  key: string;
  label: string;
  closable?: boolean;
}

export interface PanelTabBarProps {
  activeKey: string | null;
  onTabChange: (key: string) => void;
  onTabClose: (key: string) => void;
  tabs: PanelTabBarTab[];
  actions?: ReactNode;
  className?: string;
  closeAllLabel?: string;
  onCloseAll?: () => void;
}

/**
 * Panel Tab Bar Component for SplitDetailPanel
 *
 * Container component that manages multiple PanelTab components with a "Close All" button.
 * Implements the KCDS Drawer base design with horizontal tab layout.
 *
 * @example
 * ```tsx
 * <PanelTabBar
 *   tabs={[
 *     { key: 'zone:1', label: 'Zone 1' },
 *     { key: 'zone:2', label: 'Zone 2' }
 *   ]}
 *   activeKey="zone:1"
 *   onTabChange={handleTabChange}
 *   onTabClose={handleTabClose}
 *   onCloseAll={handleCloseAll}
 * />
 * ```
 */
export default function PanelTabBar(props: PanelTabBarProps) {
  const { t } = useTranslation();

  const {
    tabs,
    activeKey,
    onTabChange,
    onTabClose,
    onCloseAll,
    closeAllLabel = t('ui:action.close_all'),
    actions,
    className,
  } = props;

  const handleTabClick = useCallback(
    (tabKey: string) => {
      onTabChange(tabKey);
    },
    [onTabChange],
  );

  const handleTabClose = useCallback(
    (tabKey: string) => {
      onTabClose(tabKey);

      // 마지막 탭을 닫을 때는 모든 탭 닫기 동작과 동일하게 처리
      if (tabs.length === 1) {
        onCloseAll?.();
      }
    },
    [tabs.length, onCloseAll, onTabClose],
  );

  return (
    <PanelTabBarRoot className={className}>
      <PanelTabBarTabsContainer>
        {tabs.map(tab => (
          <PanelTab
            key={tab.key}
            label={tab.label}
            active={tab.key === activeKey}
            closable={tab.closable !== false}
            onClick={() => handleTabClick(tab.key)}
            onClose={() => handleTabClose(tab.key)}
          />
        ))}
      </PanelTabBarTabsContainer>

      <PanelTabBarActions>
        {actions}
        {onCloseAll && tabs.length > 0 && (
          <ButtonGhost size="md" endIcon={<IcCloseSmallRegular />} onClick={onCloseAll}>
            {closeAllLabel}
          </ButtonGhost>
        )}
      </PanelTabBarActions>
    </PanelTabBarRoot>
  );
}

export const panelTabBarClasses = generateUtilityClasses('PanelTabBar', [
  'root',
  'tabsContainer',
  'actions',
]);

const PanelTabBarRoot = styled('div', {
  name: 'PanelTabBar',
  slot: 'root',
  target: panelTabBarClasses.root,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  minHeight: 40,
  backgroundColor: theme.color['surface/neutral/weaker/default'],
  overflow: 'hidden',
}));

const PanelTabBarTabsContainer = styled('div', {
  name: 'PanelTabBar',
  slot: 'tabsContainer',
  target: panelTabBarClasses.tabsContainer,
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PanelTabBarActions = styled('div', {
  name: 'PanelTabBar',
  slot: 'actions',
  target: panelTabBarClasses.actions,
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  flex-shrink: 0;
`;
